import { Auth0ContextInterface, LogoutOptions, User } from "@auth0/auth0-react";
import { Auth0FrontendUser } from '@gr/shared/models';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { Auth0FrontendConfig } from './providers/auth0-data.provider';
import { endSession } from './providers/auth0-recovery.provider';
import { AUTH0_AUDIENCE, createAuth0Scope } from "./providers/auth0.service";

export interface TokenResponse {
  id_token?: string;
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  scope?: string;
}

export interface ConfigureAxiosRequest {
  auth0Config: Auth0FrontendConfig;
  auth0User: Auth0FrontendUser;
  token: TokenResponse;
  auth0Logout: (request?: LogoutOptions) => void;
  getAccessTokenSilently: Auth0ContextInterface<User>['getAccessTokenSilently'];
  loginWithRedirect: Auth0ContextInterface<User>['loginWithRedirect'];
}

interface RetriableRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

const RENEWAL_COOLDOWN_MS = 5_000;

const FORBIDDEN_REDIRECT_PATH = '/app/dashboard';

// AppWrapper declares /app/dashboard as a <Redirect> here, so the reload lands on this path
// instead. Both must be guarded or a 403 on the landing page reloads into the same 403 forever.
const FORBIDDEN_REDIRECT_LANDING_PATH = '/app/my-campaigns';

let authAxios: AxiosInstance | undefined;
let tokenResponse: TokenResponse;
let renewalPromise: Promise<string | null> | null = null;
let lastRenewalAt: number | null = null;
let isSessionEnding = false;

export function configureAxiosWithAuth0(request: ConfigureAxiosRequest) {
  tokenResponse = request.token;
  const baseURL = request.auth0Config.apiUrl;
  renewalPromise = null;
  lastRenewalAt = null;
  isSessionEnding = false;

  const instance = axios.create({ baseURL });

  instance.interceptors.request.use(
    // Must read the live module tokenResponse (not request.token) so renewals reach every request
    (config) => <any>addTokenToRequest(tokenResponse, config),
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    // No latch/budget reset on 2xx: a healthy response racing a pending session end must
    // not un-latch it. The latch resets on reconfigure or a failed redirect; the
    // sessionStorage budget resets only by window expiry.
    (response) => response,
    (error) => handleError(error, request, instance)
  );

  authAxios = instance;
  return instance;
}

function addTokenToRequest(token: TokenResponse, config: AxiosRequestConfig<any>) {
  if (token) {
    config.headers!.Authorization = `Bearer ${token.access_token}`;
  }

  return config;
}

function handleError(error: any, request: ConfigureAxiosRequest, instance: AxiosInstance) {
  const status = error?.response?.status;

  switch (status) {
    case 403:
      return handleForbidden(error);
    case 401:
      return handleUnauthorized(error, request, instance);
  }

  return Promise.reject(error);
}

function handleForbidden(error: any) {
  // A handler-level role denial carries a lowercase `message`; reject so the call site can surface
  // it rather than navigating away and discarding the user's form. An API Gateway authorizer Deny
  // is route-level and returns a capital-M `Message`, so it falls through and still redirects.
  if (typeof error?.response?.data?.message === 'string') {
    return Promise.reject(error);
  }

  const currentPath = window.location.pathname;

  // Navigating to the path we are already on would reload into the same 403 forever.
  if (currentPath === FORBIDDEN_REDIRECT_PATH || currentPath === FORBIDDEN_REDIRECT_LANDING_PATH) {
    console.error('[authAxios] 403 on the forbidden-redirect target; skipping reload to avoid a loop', error);
    return Promise.reject(error);
  }

  window.location.href = FORBIDDEN_REDIRECT_PATH;
  return Promise.reject(error);
}

async function handleUnauthorized(error: any, request: ConfigureAxiosRequest, instance: AxiosInstance) {
  if (isSessionEnding) {
    return Promise.reject(error);
  }

  const config: RetriableRequestConfig | undefined = error?.config;

  if (!config) {
    console.warn('[Auth Interceptor] 401 with no request config, ending session');
    endUnauthorizedSession(request);
    return Promise.reject(error);
  }

  if (config._retry) {
    console.warn('[Auth Interceptor] Retried request was rejected again, ending session', { url: config.url });
    endUnauthorizedSession(request);
    return Promise.reject(error);
  }

  const rejectedToken = getBearerToken(config);
  const renewedToken = await renewToken(request);

  if (!renewedToken) {
    endUnauthorizedSession(request);
    return Promise.reject(error);
  }

  if (renewedToken === rejectedToken) {
    console.warn('[Auth Interceptor] Renewal returned the rejected token, ending session', { url: config.url });
    config._retry = true;
    endUnauthorizedSession(request);
    return Promise.reject(error);
  }

  console.log('[Auth Interceptor] Retrying request with renewed token', { url: config.url });

  config._retry = true;
  config.headers = config.headers ?? {};
  config.headers.Authorization = `Bearer ${renewedToken}`;

  return instance(config);
}

function getBearerToken(config: AxiosRequestConfig): string | undefined {
  const headers: any = config.headers;
  const authHeader = typeof headers?.get === 'function' ? headers.get('Authorization') : headers?.Authorization;

  return typeof authHeader === 'string' ? authHeader.replace(/^Bearer\s+/i, '') : undefined;
}

function renewToken(request: ConfigureAxiosRequest): Promise<string | null> {
  if (renewalPromise) {
    return renewalPromise;
  }

  // Cooldown: staggered 401s (pollers, refetches) reuse the fresh module token instead
  // of each starting another refresh-token exchange
  if (lastRenewalAt && Date.now() - lastRenewalAt < RENEWAL_COOLDOWN_MS) {
    return Promise.resolve(tokenResponse?.access_token ?? null);
  }

  const { auth0Config, getAccessTokenSilently } = request;

  console.log('[Auth Interceptor] Renewing access token', { organization: auth0Config.organization });

  renewalPromise = Promise.resolve()
    .then(() => getAccessTokenSilently({
      cacheMode: 'off',
      detailedResponse: true,
      authorizationParams: {
        organization: auth0Config.organizationId,
        audience: AUTH0_AUDIENCE,
        scope: createAuth0Scope({ orgName: auth0Config.organization }),
      },
    }))
    .then((response) => {
      tokenResponse = {
        ...tokenResponse,
        id_token: response.id_token,
        access_token: response.access_token,
        expires_in: response.expires_in,
        scope: response.scope,
      };

      lastRenewalAt = Date.now();
      console.log('[Auth Interceptor] Access token renewed');
      return response.access_token;
    })
    .catch((renewalError) => {
      console.error('[Auth Interceptor] Token renewal failed', {
        error: renewalError?.error ?? 'unknown',
        description: renewalError?.error_description,
      });
      return null;
    })
    .finally(() => {
      renewalPromise = null;
    });

  return renewalPromise;
}

function endUnauthorizedSession(request: ConfigureAxiosRequest) {
  if (isSessionEnding) {
    return;
  }

  isSessionEnding = true;
  console.warn('[Auth Interceptor] Session could not be recovered, ending it');

  endSession({
    auth0Config: request.auth0Config,
    auth0Logout: request.auth0Logout,
    loginWithRedirect: request.loginWithRedirect,
    redirectOptions: {
      authorizationParams: {
        organization: request.auth0Config.organizationId,
        audience: AUTH0_AUDIENCE,
        scope: createAuth0Scope({ orgName: request.auth0Config.organization }),
      },
    },
  }).catch(() => {
    isSessionEnding = false;
  });
}

export function axiosPut<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D): Promise<R> {
  return authAxios
    ? authAxios.put<T, R, D>(url, data)
    : Promise.reject('Axios has not been configured');
}

export function axiosDelete<T = any, R = AxiosResponse<T>, D = any>(url: string): Promise<R> {
  return authAxios
    ? authAxios.delete<T, R>(url)
    : Promise.reject('Axios has not been configured');
}

export function axiosPatch<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D): Promise<R> {
  return authAxios
    ? authAxios.patch<T, R, D>(url, data)
    : Promise.reject('Axios has not been configured');
}

export function axiosPost<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
  return authAxios
    ? authAxios.post<T, R, D>(url, data, config)
    : Promise.reject('Axios has not been configured');
}

export function axiosGet<T = any, R = AxiosResponse<T>, D = any>(url: string): Promise<R> {
  return authAxios
    ? authAxios.get<T, R>(url)
    : Promise.reject('Axios has not been configured');
}
